import { useEffect, useMemo, useState } from 'react'
import { Dropdown } from './ui'

const DEFAULT_SIZES = [10, 25, 50, 100] as const

export function usePagination<T>(items: T[], defaultSize = 25) {
  const [page, setPage] = useState(0)
  const [size, setSize] = useState<number>(defaultSize)
  const total = items.length
  const totalPages = Math.max(1, Math.ceil(total / size))

  useEffect(() => {
    if (page > totalPages - 1) setPage(0)
  }, [totalPages, page])

  const pageItems = useMemo(
    () => items.slice(page * size, (page + 1) * size),
    [items, page, size],
  )

  return { page, setPage, size, setSize, total, totalPages, pageItems }
}

interface BarProps {
  page: number
  setPage: (p: number) => void
  size: number
  setSize: (s: number) => void
  total: number
  totalPages: number
  sizes?: readonly number[]
  /** Text of one page-size choice (default: the number), e.g. "Alle" for a very large size. */
  sizeLabel?: (size: number) => string
}

export function PaginationBar({ page, setPage, size, setSize, total, totalPages, sizes = DEFAULT_SIZES, sizeLabel }: BarProps) {
  if (total === 0) return null

  const start = page * size + 1
  const end = Math.min((page + 1) * size, total)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-xs text-on-surface-variant bg-surface-container-low border-t border-outline-variant/40">
      <div className="flex items-center gap-3">
        <span className="tabular-nums">{start}–{end} von {total}</span>
        <div className="flex items-center gap-1.5">
          <span>pro Seite:</span>
          <div style={{ minWidth: 70 }}>
            <Dropdown
              size="compact"
              usePortal
              value={String(size)}
              onChange={v => { setSize(Number(v)); setPage(0) }}
              options={sizes.map(s => ({ value: String(s), label: sizeLabel ? sizeLabel(s) : String(s) }))}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => setPage(0)}
          disabled={page === 0}
          className="rounded-lg border border-outline-variant bg-surface-container-lowest text-on-surface px-2.5 py-1 font-medium disabled:opacity-30 hover:bg-surface-container"
          title="Erste Seite"
        >«</button>
        <button
          onClick={() => setPage(Math.max(0, page - 1))}
          disabled={page === 0}
          className="rounded-lg border border-outline-variant bg-surface-container-lowest text-on-surface px-2.5 py-1 font-medium disabled:opacity-30 hover:bg-surface-container"
          title="Zurück"
        >‹</button>
        <span className="px-3 tabular-nums">{page + 1} / {totalPages}</span>
        <button
          onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
          disabled={page >= totalPages - 1}
          className="rounded-lg border border-outline-variant bg-surface-container-lowest text-on-surface px-2.5 py-1 font-medium disabled:opacity-30 hover:bg-surface-container"
          title="Weiter"
        >›</button>
        <button
          onClick={() => setPage(totalPages - 1)}
          disabled={page >= totalPages - 1}
          className="rounded-lg border border-outline-variant bg-surface-container-lowest text-on-surface px-2.5 py-1 font-medium disabled:opacity-30 hover:bg-surface-container"
          title="Letzte Seite"
        >»</button>
      </div>
    </div>
  )
}
